// AuthContext.js
import { jwtDecode } from "jwt-decode";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  loginAdmin,
  loginRestaurant,
  loginByInvite as loginByInviteService,
  bootstrapSuperAdmin as bootstrapSuperAdminService,
  requestEmailVerification as requestEmailVerificationService,
  verifyEmailCode as verifyEmailCodeService,
  logout as logoutService,
} from "../services/authService";
import axiosInstance from "../services/axiosInstance";
import {
  defaultPrimaryColor,
  defaultSecondaryColor,
  defaultTertiaryColor,
  useThemeContext,
} from "../theme/ThemeProvider";
import { getToken, getUserRole, isTokenValid } from "../utils/tokenUtils";
import { useLoading } from "./LoadingContext";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const initialState = {
  restaurantLogo: "HoloMenu",
  splashScreenBg: "#f5f5f5",
  themeBg: "",
  language: { language_id: 0, language_name: "English" },
};

export const AuthProvider = ({ children }) => {
  const _token = useMemo(() => getToken(), []);

  const [auth, setAuth] = useState({
    token: _token,
    role: getUserRole(),
    user: _token && isTokenValid() ? jwtDecode(_token) : null,
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => _token !== null);
  const [restaurantLogo, setRestaurantLogo] = useState(
    initialState.restaurantLogo
  ); // Default logo
  const [splashScreenBg, setSplashScreenBg] = useState(
    initialState.splashScreenBg
  );
  const [themeBg, setThemeBg] = useState(initialState.themeBg);

  // Language-related states
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(
    initialState.language
  );

  const navigate = useNavigate();
  const location = useLocation();
  const { startLoading, stopLoading } = useLoading();
  const { updateTheme } = useThemeContext();

  // Pages that should always use the platform's default brand colors instead
  // of inheriting the restaurant's theme.
  const adminPathPrefixes = [
    "/dashboard",
    "/analytics",
    "/super-admin",
  ];
  const isAdminPath = (pathname) =>
    adminPathPrefixes.some(
      (p) => pathname === p || pathname.startsWith(`${p}/`)
    );

  useEffect(() => {
    const token = getToken();
    const storedLanguage = localStorage.getItem("selectedLanguage");

    if (token && isTokenValid()) {
      const decodedToken = jwtDecode(token);
      setAuth({ token, role: getUserRole(), user: decodedToken });
      if (storedLanguage) {
        changeLanguage(JSON.parse(storedLanguage));
      }
      // Theme is owned by the path-based effect below.
      setRestaurantLanguages(decodedToken);
      setIsAuthenticated(true);
    } else {
      logoutService();
      setAuth({ token: null, role: null, user: null });
      setLanguages([]);
      setSelectedLanguage(initialState.language);
      setIsAuthenticated(false);
      resetToDefaultTheme();
      // Only redirect to /login if the user isn't already on a public auth page.
      const publicPaths = ["/login", "/super-admin/login"];
      if (!publicPaths.includes(window.location.pathname)) {
        navigate("/login");
      }
    }
  }, []);

  useEffect(() => {
    if (auth?.user?.restaurant?.theme) {
      const storedLanguage = localStorage.getItem("selectedLanguage");
      if (storedLanguage) {
        changeLanguage(JSON.parse(storedLanguage));
      }
    }
  }, [auth]);

  // Switch theme based on the page being viewed: admin/super-admin pages use
  // the default brand palette; customer-facing pages use the restaurant theme.
  useEffect(() => {
    if (isAdminPath(location.pathname)) {
      resetToDefaultTheme();
    } else if (auth?.user?.restaurant) {
      setRestaurantTheme(auth.user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, auth?.token]);

  const resetToDefaultTheme = () => {
    updateTheme(
      defaultPrimaryColor,
      defaultSecondaryColor,
      defaultTertiaryColor
    );
    setRestaurantLogo(initialState.restaurantLogo);
    setSplashScreenBg(initialState.splashScreenBg);
    setThemeBg(initialState.themeBg);
  };

  const setRestaurantLanguages = (decodedToken) => {
    const serverLanguages = decodedToken.restaurant.languages || [];
    const updatedLanguages = [...serverLanguages];
    setLanguages(updatedLanguages);
  };

  const setRestaurantTheme = (decodedToken) => {
    const { restaurant } = decodedToken;
    const { theme } = restaurant || {};

    // Extract theme colors and URLs
    const { primary_color, secondary_color, tertiary_color, font_url } =
      theme || {};

    setRestaurantLogo(restaurant.logo || initialState.restaurantLogo);
    setSplashScreenBg(theme?.splash_screen_bg || initialState.splashScreenBg);
    setThemeBg(theme?.theme_bg || initialState.themeBg);

    // Set default font URL for the theme, if available
    const defaultFontUrl = font_url || null;

    // Update MUI theme with colors and default font URL
    updateTheme(primary_color, secondary_color, tertiary_color, defaultFontUrl);
  };

  const changeLanguage = (language) => {
    const selectedLang =
      language || languages.find((lang) => lang.language_name === "English");

    setSelectedLanguage(selectedLang);

    // Save the selected language to local storage
    localStorage.setItem("selectedLanguage", JSON.stringify(selectedLang));

    // Extract current theme from auth.user
    const theme = auth?.user?.restaurant?.theme;

    // Determine font URL based on language
    let fontUrl = theme?.font_url; // Default font (English)
    if (selectedLang.language_name !== "English" && theme?.fonts) {
      const matchingFont = theme.fonts.find(
        (font) => font.language === selectedLang.language_id
      );
      fontUrl = matchingFont ? matchingFont.font_url : fontUrl;
    }

    // Update theme with new font URL, keeping colors unchanged
    const { primary_color, secondary_color, tertiary_color } = theme || {};
    updateTheme(primary_color, secondary_color, tertiary_color, fontUrl);
  };

  const loginUser = async (restaurantName, redirect) => {
    startLoading();
    try {
      const response = await loginRestaurant(restaurantName);
      const token = response?.data?.token;
      if (token) {
        localStorage.setItem("token", token);
        const decodedToken = jwtDecode(token);
        setAuth({ token, role: "user", user: decodedToken });
        setIsAuthenticated(true);
        setRestaurantLanguages(decodedToken);
        navigate(redirect ?? "/startup");
      }
    } catch (error) {
      throw error;
    } finally {
      stopLoading();
    }
  };

  const bootstrapSuperAdmin = async () => {
    startLoading();
    try {
      const response = await bootstrapSuperAdminService();
      const token = response?.data?.token;
      if (token) {
        localStorage.setItem("token", token);
        const decodedToken = jwtDecode(token);
        setAuth({ token, role: "user", user: decodedToken });
        setIsAuthenticated(true);
      }
    } catch (error) {
      throw error;
    } finally {
      stopLoading();
    }
  };

  const loginByInvite = async (inviteToken) => {
    startLoading();
    try {
      const response = await loginByInviteService(inviteToken);
      const token = response?.data?.token;
      const email = response?.data?.email;
      if (token) {
        localStorage.setItem("token", token);
        const decodedToken = jwtDecode(token);
        setAuth({ token, role: "user", user: decodedToken });
        setIsAuthenticated(true);
        setRestaurantLanguages(decodedToken);
      }
      return { email };
    } catch (error) {
      throw error;
    } finally {
      stopLoading();
    }
  };

  const loginAdminUser = async (username, password) => {
    startLoading();
    try {
      const response = await loginAdmin(username, password);
      const token = response?.data?.token;
      if (token) {
        localStorage.setItem("token", token);
        const decodedToken = jwtDecode(token);
        setAuth({ token, role: decodedToken.role, user: decodedToken });
        setIsAuthenticated(true);
        setRestaurantLanguages(decodedToken);
        navigate("/dashboard");
      }
    } catch (error) {
      throw error;
    } finally {
      stopLoading();
    }
  };

  const logout = () => {
    startLoading();
    setTimeout(() => {
      logoutService();
      resetToDefaultTheme();
      setAuth({ token: null, role: null, user: null });
      setIsAuthenticated(false);
      setLanguages([]);
      setSelectedLanguage(initialState.language);
      axiosInstance.storage.clear();
      navigate("/login");
      stopLoading();
    }, 1000);
  };

  const requestEmailVerification = async (email) => {
    startLoading();
    try {
      const response = await requestEmailVerificationService(email);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      stopLoading();
    }
  };

  const verifyEmailCode = async (email, code, redirectTo = "/dashboard") => {
    startLoading();
    try {
      const response = await verifyEmailCodeService(email, code);
      const token = response?.data?.token;
      if (token) {
        localStorage.setItem("token", token);
        const decodedToken = jwtDecode(token);
        setAuth({ token, role: decodedToken.role, user: decodedToken });
        setIsAuthenticated(true);
        setRestaurantLanguages(decodedToken);
        navigate(redirectTo);
      }
    } catch (error) {
      throw error;
    } finally {
      stopLoading();
    }
  };

  const value = {
    auth,
    isAuthenticated,
    loginUser,
    loginByInvite,
    bootstrapSuperAdmin,
    loginAdminUser,
    logout,
    restaurantLogo,
    splashScreenBg,
    themeBg,
    languages,
    selectedLanguage,
    changeLanguage,
    requestEmailVerification,
    verifyEmailCode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
